import { useMemo, useEffect, useContext, useState } from 'react';
import { IconButton } from '@mui/material';
import {
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';
import {
  LoadingSpinner,
  NoFlyersAvailableSectionEmptyState,
  PersistentTooltip,
  ViewMoreCard
} from '@/components';
import {
  BodyLargeEmphasized,
} from '@/design-system';
import { AllFlyerFilters } from '@/graphql/types';

import { useFlyerboardPagination, useGoogleTranslator } from '@/hooks';
import { isMobile } from '@/utils/isMobile';
import { FlyerList } from './FlyerList';
import styles from './FlyerboardPagination.styles';
import colors from '@/theme/colors';
import UserPreferencesContext from '@/context/UserPreferencesContext';
import AuthenticationContext from '@/context/AuthenticationContext';
import { CommunityDisclaimerModal } from '../CommunityDisclaimerModal';

type Props = {
  trackId: string;
  flyerboardFilters: AllFlyerFilters;
  flyerType: string;
  audienceId?: string;
  hasMyCommSchoolFlyers: boolean;
  setHasOtherSchoolFlyers: (hasOtherSchoolFlyers: boolean) => void;
}

const WHOLE_COMMUNITY_TOOLTIP = `My whole community consists of more opportunities intended for my child's grade/age
that are distributed through all schools located in my community area.`;

export const OtherSchoolFlyers: React.FC<Props> = ({
  trackId,
  flyerboardFilters,
  flyerType,
  audienceId,
  hasMyCommSchoolFlyers,
  setHasOtherSchoolFlyers,
}) => {
  const tooltipTranslated = useGoogleTranslator(WHOLE_COMMUNITY_TOOLTIP);
  const { community, viewWholeCommunityFlyers } = useContext(UserPreferencesContext);

  const { isAuthenticated } = useContext(AuthenticationContext);
  const [showCommDisclaimerModal, setShowCommDisclaimerModal] =
  useState<boolean>(false);

  const { lat, lng } = community?.location;

  const otherSchoolFilters: AllFlyerFilters = {
    ...flyerboardFilters,
    allDistrictSchools: true,
    communityLocation: (isAuthenticated && community?.location) ? { lat, lng } : undefined,
    communityRadius: (isAuthenticated && community.distance) ? community.distance : undefined,
  }

  const {
    setPage,
    flyers,
    resetPagination,
    favoriteFlyer,
    dataNotFound,
    loading,
    total
  } = useFlyerboardPagination({
    trackId,
    flyerboardFilters: otherSchoolFilters,
    flyerType,
    shouldUpdateFacets: false,
    audienceId,
  });

  useEffect(() => {
    if (total === 0) {
      setHasOtherSchoolFlyers(false);
    }
  }, [total])

  const showHeader = !loading && (!dataNotFound || hasMyCommSchoolFlyers);

  const nextPage = () =>  setPage((prevPage) => prevPage + 1);

  const onFavoriteFlyer = (flyerId: number) => favoriteFlyer(flyerId);

  const onSeeMoreFlyersClick = () => {
    setShowCommDisclaimerModal(true);
  };

  const onConfirmCommDisclaimer = () => {
    onCancelCommDisclaimer();
  };

  const onCancelCommDisclaimer = () => {
    setShowCommDisclaimerModal(false);
  };

  const EmptyState = () => {
    if( loading || flyers.length > 0 || !hasMyCommSchoolFlyers) return null;

    return <NoFlyersAvailableSectionEmptyState />
  }

  const CommunityToolTip = () => (
    <span className={styles.tooltipButton}>
      <PersistentTooltip title={tooltipTranslated}>
        <IconButton arial-label="About My Whole Community">
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
      </PersistentTooltip>
    </span>
  );

  const showWholeCommBanner = viewWholeCommunityFlyers === null && flyers.length > 0;

  if (showWholeCommBanner) {
    return (
      <>
        <ViewMoreCard
          title="MY WHOLE COMMUNITY"
          description="Open up my whole community to view all opportunities fitting for my child."
          buttonLabel="View my whole community"
          onClick={onSeeMoreFlyersClick}
        />
        <CommunityDisclaimerModal
          isOpen={showCommDisclaimerModal}
          onConfirm={onConfirmCommDisclaimer}
          onCancel={onCancelCommDisclaimer}
        />
      </>
    )
  }

  if(viewWholeCommunityFlyers === null) return null;

  return (
    <div className={styles.container}>
      <div className={styles.section}>
        {loading && flyers.length === 0 && <LoadingSpinner />}
        {showHeader && (
          <>
            <BodyLargeEmphasized color={colors.slate}>
              My Whole Community
            </BodyLargeEmphasized>
            <CommunityToolTip />
          </>
        )}
        <EmptyState />
        <FlyerList
          trackId={trackId}
          flyers={flyers}
          nextPage={nextPage}
          onFavoriteFlyer={onFavoriteFlyer}
          total={total}
          audienceId={audienceId}
        />
      </div>
    </div>
  )
};
